import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  HlsPlayer,
  NativePlayer,
  nativePip,
  nativePlayerBridge,
  nativePlayerCapabilities,
  type Player,
  type PlayerState,
} from "@hybrid-iptv/player";
import { Button, LiveBadge, useBackLayer } from "@hybrid-iptv/ui";
import type { Channel } from "@hybrid-iptv/types";
import {
  MAX_MULTIVIEW_TILES,
  useMultiviewStore,
  type MultiviewTile,
} from "./multiviewStore.js";
import { usePlayerStore } from "./playerStore.js";
import { PlaybackTimeMeter } from "../library/playbackUsageStore.js";
import { showUpgradePrompt } from "../library/upgradePrompt.js";
import { useChannels } from "../epg/hooks.js";
import { useActiveProviderSourceId } from "../epg/api.js";
import { dvr, useDvr } from "../dvr/dvr.js";
import { otherDeviceStreams, tileLimit, tileLimitMessage } from "./multiviewLimit.js";


/** The native player slot for a tile (NativePlayerPlugin keys players by id). */
function tilePlayerId(tileId: string): string {
  return `tile-${tileId}`;
}

/** What was asked once as the grid opened (Android app only). */
interface OpenBudget {
  deviceTiles: number;
  maxConnections: number;
  otherDevices: number;
}

/**
 * Section 14 step 3 — the multiview grid. Rendered above everything when the
 * store is active. Each tile owns an INDEPENDENT HlsPlayer on its own <video>
 * element, so every tile is one provider connection; the grid is capped at
 * MAX_MULTIVIEW_TILES and the server gate is set to match the panel allowance.
 *
 * Exit handoff: the root component stays mounted while the grid unmounts, so
 * React runs the tile players' destroy cleanups (releasing every connection)
 * BEFORE the handoff effect hands the primary tile's source to the main
 * player — the channel keeps playing in the corner mini-player, one connection
 * afterwards.
 *
 * Inside the Android app each tile is a NativePlayer slot (its own ExoPlayer drawn
 * behind the page, see NativePlayerPlugin), the page is see-through over the tiles
 * (app.css, html.native-multiview), and the grid's size is what the device, the
 * provider's limit, the viewer's other devices and recordings allow
 * (multiviewLimit.ts). Picture-in-picture shows the main tile.
 */
export function MultiviewOverlay() {
  const active = useMultiviewStore((s) => s.active);
  const pendingHandoffRef = useRef<PlayerState["source"] | null>(null);
  const openedRef = useRef(false);

  // Handoff AFTER tile teardown: child unmount cleanups (player.destroy →
  // sockets closed → server gate slots released) run before this parent
  // effect, so the primary's new connection never stacks on top of its tile's.
  useEffect(() => {
    if (openedRef.current && !active && pendingHandoffRef.current) {
      const source = pendingHandoffRef.current;
      pendingHandoffRef.current = null;
      void usePlayerStore.getState().playSource({ ...source, preview: true });
    }
    openedRef.current = true;
  }, [active]);

  const exitNow = useCallback(() => {
    const state = useMultiviewStore.getState();
    if (!state.active) return;
    const primary = state.tiles.find((tile) => tile.id === state.primaryId);
    if (primary) pendingHandoffRef.current = primary.source;
    state.exit();
  }, []);

  // Back leaves the grid instead of the page underneath it. The back stack owns
  // the history entry and unwinds it whichever way the grid closes — Back, the
  // Exit button, or the last tile being removed (@hybrid-iptv/ui backStack).
  useBackLayer(active, exitNow);

  const handleExit = useCallback(() => exitNow(), [exitNow]);

  // Android app: the page goes see-through over the tiles, and picture-in-picture
  // goes back to the main player once the grid closes.
  useEffect(() => {
    const bridge = nativePlayerBridge();
    if (!bridge || !active) return;
    const root = document.documentElement;
    root.classList.add("native-multiview");
    return () => {
      root.classList.remove("native-multiview");
      void nativePip.setSource(bridge, "main");
    };
  }, [active]);

  // Escape closes the grid like the Exit button (TV remote Back).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleExit();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [handleExit]);

  if (!active) return null;

  return (
    <>
      {nativePlayerBridge() && <NativeTileBudget />}
      <MultiviewGrid onExit={handleExit} />
    </>
  );
}

/**
 * Android app: how many tiles fit. Asks the device and the provider once as the
 * grid opens, then follows recordings as they start. A recording wins: the
 * tile whose stream it needs is removed, with a notice.
 */
function NativeTileBudget() {
  const setLimit = useMultiviewStore((s) => s.setLimit);
  const enforceLimit = useMultiviewStore((s) => s.enforceLimit);
  const primaryId = useMultiviewStore((s) => s.primaryId);
  const { state: dvrState } = useDvr();
  const [budget, setBudget] = useState<OpenBudget | null>(null);
  const recordingNow = dvrState ? dvrState.recordings.filter((r) => r.status === "recording").length : 0;
  const previousRecordingNow = useRef(recordingNow);

  useEffect(() => {
    const bridge = nativePlayerBridge();
    if (!bridge) return;
    let cancelled = false;
    setLimit(null, "Checking how many streams your provider allows…");
    const unknown = { maxConnections: 0, activeConnections: -1, recordingNow: 0 };
    const connections = Promise.race([
      dvr.connections().catch(() => unknown),
      new Promise<typeof unknown>((resolve) => setTimeout(() => resolve(unknown), 5_000)),
    ]);
    void Promise.all([nativePlayerCapabilities(bridge), connections]).then(([capabilities, account]) => {
      if (cancelled) return;
      // What this device had open when asked: the channel the grid opened with,
      // plus its recordings. The provider may still be counting it.
      const ownOpen = account.recordingNow + 1;
      setBudget({
        deviceTiles: capabilities.deviceTiles,
        maxConnections: account.maxConnections,
        otherDevices: otherDeviceStreams(account, ownOpen),
      });
    });
    return () => {
      cancelled = true;
    };
  }, [setLimit]);

  useEffect(() => {
    if (!budget) return;
    const input = {
      deviceTiles: budget.deviceTiles,
      maxConnections: budget.maxConnections || dvrState?.maxConnections || 0,
      recordingNow,
      otherDevices: budget.otherDevices,
    };
    const limit = tileLimit(input);
    setLimit(limit.limit, tileLimitMessage(limit, input));
    if (recordingNow > previousRecordingNow.current) enforceLimit("a recording started and needed that stream.");
    previousRecordingNow.current = recordingNow;
  }, [budget, recordingNow, dvrState?.maxConnections, setLimit, enforceLimit]);

  useEffect(() => {
    const bridge = nativePlayerBridge();
    if (bridge && primaryId) void nativePip.setSource(bridge, tilePlayerId(primaryId));
  }, [primaryId]);

  return null;
}

function MultiviewGrid({ onExit }: { onExit: () => void }) {
  const tiles = useMultiviewStore((s) => s.tiles);
  const addChannel = useMultiviewStore((s) => s.addChannel);
  const limit = useMultiviewStore((s) => s.limit);
  const notice = useMultiviewStore((s) => s.notice);
  const dismissNotice = useMultiviewStore((s) => s.dismissNotice);
  const shownLimit = limit ?? MAX_MULTIVIEW_TILES;
  // The channel list takes the bottom of the screen; hiding it gives the tiles the full height.
  const [pickerOpen, setPickerOpen] = useState(true);

  return (
    <div className="multiview" role="dialog" aria-modal="true" aria-label="Multiview">
      <div className="multiview__header">
        <span className="multiview__title">Multiview</span>
        <span className="multiview__count" aria-label={`${tiles.length} of ${shownLimit} tiles`}>
          {tiles.length}/{shownLimit}
        </span>
        <Button variant="ghost" onClick={() => setPickerOpen((open) => !open)} aria-expanded={pickerOpen}>
          {pickerOpen ? "Hide channels" : "Show channels"}
        </Button>
        <Button variant="ghost" onClick={onExit} aria-label="Exit multiview">
          ✕ Exit
        </Button>
      </div>

      {notice && (
        <div className="multiview__notice" role="status">
          <span>{notice}</span>
          <Button variant="ghost" onClick={dismissNotice} aria-label="Dismiss">
            ✕
          </Button>
        </div>
      )}

      <div className={`multiview__grid multiview__grid--${Math.min(tiles.length, MAX_MULTIVIEW_TILES)}`}>
        {tiles.map((tile) => (
          <MultiviewTileView key={tile.id} tile={tile} />
        ))}
      </div>

      {pickerOpen && <ChannelPicker tiles={tiles} onAdd={(channel) => void addChannel(channel)} />}
    </div>
  );
}

function MultiviewTileView({ tile }: { tile: MultiviewTile }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const playerRef = useRef<Player | null>(null);
  const setTileState = useMultiviewStore((s) => s.setTileState);
  const setPrimary = useMultiviewStore((s) => s.setPrimary);
  const removeTile = useMultiviewStore((s) => s.removeTile);
  const isPrimary = useMultiviewStore((s) => s.primaryId === tile.id);
  const quotaStoppedRef = useRef(false);

  // One independent player per tile; created once per tile mount. The store
  // resolved the source BEFORE the tile rendered, so load() opens the stream
  // connection immediately.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    // Android app: a native player slot of its own, drawn behind the tile.
    const bridge = nativePlayerBridge();
    const player: Player = bridge
      ? new NativePlayer({ video, bridge, playerId: tilePlayerId(tile.id) })
      : new HlsPlayer({ video });
    playerRef.current = player;
    player.setMuted(tile.muted);
    // Secondary tiles are independent live players, so each gets its own
    // active-playback meter. The main player is closed before this grid opens;
    // on exit the tile is destroyed before the main player is handed back, so
    // the primary is never counted twice during handoff.
    const meter = new PlaybackTimeMeter("live");
    quotaStoppedRef.current = false;
    const unsubscribe = player.on("statechange", (payload) => {
      const state = payload as PlayerState;
      const exhausted = meter.sample(state.status);
      if (exhausted && state.status === "playing" && !quotaStoppedRef.current) {
        quotaStoppedRef.current = true;
        setTileState(tile.id, {
          status: "error",
          error: "Free live playback limit reached. Unlock Paid mode to continue.",
        });
        player.stop();
        showUpgradePrompt("live");
        return;
      }
      setTileState(tile.id, { status: state.status, error: state.error?.message });
    });
    player
      .load(tile.source)
      .then(() => player.play())
      .catch((err: unknown) => {
        setTileState(tile.id, {
          status: "error",
          error: err instanceof Error ? err.message : "Playback failed",
        });
      });
    return () => {
      unsubscribe();
      player.destroy();
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tile.id]);

  // Only the primary tile is audible; secondaries stay muted (also what lets
  // them autoplay without a gesture).
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = tile.muted;
    playerRef.current?.setMuted(tile.muted);
  }, [tile.muted]);

  const retry = useCallback(() => {
    if (quotaStoppedRef.current) return;
    const player = playerRef.current;
    if (!player) return;
    setTileState(tile.id, { status: "loading" });
    player
      .load(tile.source)
      .then(() => player.play())
      .catch(() => setTileState(tile.id, { status: "error", error: "Playback failed" }));
  }, [tile.id, tile.source, setTileState]);

  return (
    <div className={`multiview__tile ${isPrimary ? "multiview__tile--primary" : ""}`}>
      <video ref={videoRef} className="multiview__tile-video" playsInline autoPlay muted={tile.muted} />
      <div className="multiview__tile-badges">
        {isPrimary && <span className="multiview__tile-badge multiview__tile-badge--primary">MAIN</span>}
        <span className="multiview__tile-badge">
          <LiveBadge label={tile.status === "playing" ? "LIVE" : tile.status.toUpperCase()} />
        </span>
      </div>
      <div className="multiview__tile-actions">
        {!isPrimary && (
          <Button variant="ghost" onClick={() => setPrimary(tile.id)} aria-label={`Make ${tile.channel.name} main`} title="Make main">
            ⭐
          </Button>
        )}
        <Button variant="ghost" onClick={() => removeTile(tile.id)} aria-label={`Remove ${tile.channel.name}`} title="Remove">
          ✕
        </Button>
      </div>
      <div className="multiview__tile-title" title={tile.channel.name}>
        {tile.channel.name}
      </div>
      {tile.status === "error" && (
        <div className="multiview__tile-error">
          <span>{tile.error ?? "Playback failed"}</span>
          <Button variant="ghost" onClick={retry} aria-label={`Retry ${tile.channel.name}`}>
            ↻ Retry
          </Button>
        </div>
      )}
    </div>
  );
}

function ChannelPicker({
  tiles,
  onAdd,
}: {
  tiles: MultiviewTile[];
  onAdd: (channel: Channel) => void;
}) {
  const [query, setQuery] = useState("");
  // The active provider's id, like every other page — so this shares their
  // channel query and device cache instead of holding a second copy.
  const { data } = useChannels(useActiveProviderSourceId());
  const channels = data?.channels ?? [];
  const tileChannelIds = useMemo(() => new Set(tiles.map((t) => t.channel.id)), [tiles]);
  const limit = useMultiviewStore((s) => s.limit);
  const limitNote = useMultiviewStore((s) => s.limitNote);
  const checking = limit === null;
  const full = checking || tiles.length >= Math.min(limit, MAX_MULTIVIEW_TILES);

  // Mainstream US networks first, then other US channels, then everything else.
  // The picker's natural number order starts with 24/7 filler + UK channels,
  // and a blanket US-first sort surfaces niche US originals (Paramount+,
  // hulu…) before the networks viewers actually watch — on this provider the
  // "Usa Ultra Hd Exclusive" category IS the common-network lineup (ACC, AMC,
  // BBC News, BET, Bloomberg, CBS/ABC/NBC/Fox, ESPN, …). When the user types a
  // search, relevance wins and the ranking is dropped.
  const rankCategory = useCallback((id: string): number => {
    const lower = id.toLowerCase();
    if (lower.startsWith("usa-ultra-hd")) return 0; // mainstream UHD networks
    if (lower.startsWith("usa") || lower.startsWith("us ") || lower.startsWith("us-")) return 1;
    return 2;
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = q
      ? channels.filter(
          (c) => c.name.toLowerCase().includes(q) || (c.number != null && String(c.number) === q),
        )
      : channels;
    const ranked = q
      ? matches
      : [...matches].sort((a, b) => {
          const ar = rankCategory(a.categoryId ?? "");
          const br = rankCategory(b.categoryId ?? "");
          return ar - br || (a.number ?? 9999) - (b.number ?? 9999);
        });
    return ranked.slice(0, 60);
  }, [channels, query, rankCategory]);

  return (
    <div className="multiview__picker">
      <div className="multiview__picker-search">
        <input
          className="multiview__picker-input"
          type="search"
          placeholder="Search channels to add…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search channels"
        />
        {full && (
          <span className="multiview__picker-note">
            {checking
              ? limitNote
              : tiles.length >= MAX_MULTIVIEW_TILES || !limitNote
                ? "Grid full — remove a tile to add more"
                : `${limitNote}. Remove a tile to swap channels.`}
          </span>
        )}
      </div>
      <ul className="multiview__picker-list" role="listbox" aria-label="Channels">
        {filtered.map((channel) => {
          const already = tileChannelIds.has(channel.id);
          return (
            <li key={channel.id} role="option" aria-selected={already}>
              <button
                className="multiview__picker-item"
                disabled={already || full}
                onClick={() => onAdd(channel)}
              >
                <span className="multiview__picker-num">{channel.number ?? ""}</span>
                {channel.logoUrl && (
                  <img className="multiview__picker-logo" src={channel.logoUrl} alt="" loading="lazy" />
                )}
                <span className="multiview__picker-name">{channel.name}</span>
                {already && <span className="multiview__picker-check">✓ in grid</span>}
              </button>
            </li>
          );
        })}
        {filtered.length === 0 && <li className="multiview__picker-empty">No channels match</li>}
      </ul>
    </div>
  );
}
